<header id="global-header" class="fb-global-header" role="banner">
  <div class="fb-global-header__inner">
    @if (viewModel().helpArticleId || viewModel().breadcrumbs.length > 1) {
      @if (viewModel().breadcrumbs.length > 1) {
        <div class="fb-global-header__top">
          <nav class="fb-global-header__breadcrumbs" aria-label="Breadcrumb">
            <ol class="fb-global-header__breadcrumb-list">
              @for (
                crumb of viewModel().breadcrumbs;
                track crumb.url ?? crumb.label;
                let i = $index
              ) {
                <li
                  class="fb-global-header__breadcrumb-item"
                  [matTooltip]="crumb.label"
                  [matTooltipDisabled]="crumb.label.length <= 20">
                  @if (crumb.url === viewModel().breadcrumbs.at(-1)?.url) {
                    <span
                      aria-current="page"
                      class="fb-global-header__breadcrumb-name current-page"
                      >{{ crumb.label }}</span
                    >
                  } @else {
                    @if (crumb.url) {
                      <a
                        [routerLink]="crumb.url"
                        class="fb-global-header__breadcrumb-name"
                        [attr.data-testid]="'link-breadcrumb-' + i"
                        >{{ crumb.label }}</a
                      >
                    } @else {
                      <span class="fb-global-header__breadcrumb-name">{{ crumb.label }}</span>
                    }
                  }
                </li>
              }
            </ol>
          </nav>
        </div>
      }
    }

    @if (viewModel().title) {
      <h1 class="fb-global-header__title">
        <span class="fb-global-header__title-text" data-testid="text-page-title">{{
          viewModel().title
        }}</span>
        @if (viewModel().titleCount; as titleCount) {
          <span class="fb-global-header__title-count">{{ titleCount }}</span>
        }
      </h1>
    }
  </div>
  <div class="fb-global-header__actions">
    <app-context-switcher class="fb-global-header__context-switcher" />

    @if (isCompact()) {
      <!--
        Compact: everything the page put in the header folds into one overflow menu. A menu of
        commands is what MatMenu is for, and it keeps the bar down to the switcher plus one
        button no matter how many actions a page registers. The menu is never empty -- the theme
        toggle is in it on every page.
      -->
      <button
        type="button"
        mat-icon-button
        class="fb-global-header__action fb-icon-button--filled"
        [matMenuTriggerFor]="overflowMenu"
        [attr.aria-label]="'SHARED.SYSTEM.MORE_ACTIONS' | translate"
        data-testid="btn-header-overflow">
        <mat-icon aria-hidden="true">more_vert</mat-icon>
      </button>

      <mat-menu #overflowMenu="matMenu">
        <button type="button" mat-menu-item data-testid="btn-toggle-theme" (click)="toggleTheme()">
          <mat-icon aria-hidden="true">{{ isDarkMode() ? 'light_mode' : 'dark_mode' }}</mat-icon>
          <span>{{ themeToggleLabel() }}</span>
        </button>

        @for (action of visibleActions(); track action.id) {
          <button
            type="button"
            mat-menu-item
            [disabled]="action.disabled === true"
            [attr.data-testid]="action.id"
            (click)="action.onTrigger()">
            @if (action.icon) {
              <mat-icon aria-hidden="true">{{ action.icon }}</mat-icon>
            }
            <span>{{ actionLabel(action) }}</span>
          </button>
        }

        @if (viewModel().printEnabled) {
          <button type="button" mat-menu-item data-testid="btn-print-page" (click)="printPage()">
            <mat-icon aria-hidden="true">print</mat-icon>
            <span>{{ 'SHARED.SYSTEM.PRINT_PAGE' | translate }}</span>
          </button>
        }

        @if (viewModel().helpArticleId; as helpArticleId) {
          <button
            type="button"
            mat-menu-item
            data-testid="btn-help"
            (click)="openHelpArticle(helpArticleId)">
            <mat-icon aria-hidden="true">help_outline</mat-icon>
            <span>{{ helpLabel() }}</span>
          </button>
        }
      </mat-menu>
    } @else {
      <div class="fb-global-header__action-group">
        @for (action of viewModel().actions; track action.id) {
          @if (action.visible !== false) {
            <span
              [matTooltip]="
                action.tooltip ??
                (action.tooltipTranslateKey ? (action.tooltipTranslateKey | translate) : null)
              ">
              @if (action.kind === 'icon') {
                <button
                  type="button"
                  mat-icon-button
                  class="fb-global-header__action fb-icon-button--filled"
                  [disabled]="action.disabled === true"
                  [attr.data-testid]="action.id"
                  [matTooltip]="
                    action.label ??
                    (action.labelTranslateKey ? (action.labelTranslateKey | translate) : '')
                  "
                  (click)="action.onTrigger()"
                  [attr.aria-label]="
                    action.ariaLabel ??
                    (action.ariaLabelTranslateKey
                      ? (action.ariaLabelTranslateKey | translate)
                      : null)
                  ">
                  <mat-icon aria-hidden="true">{{ action.icon }}</mat-icon>
                </button>
              } @else {
                <button
                  type="button"
                  matButton="filled"
                  class="fb-global-header__action"
                  [disabled]="action.disabled === true"
                  [attr.data-testid]="action.id"
                  (click)="action.onTrigger()"
                  [attr.aria-label]="
                    action.ariaLabel ??
                    (action.ariaLabelTranslateKey
                      ? (action.ariaLabelTranslateKey | translate)
                      : null)
                  ">
                  {{
                    action.label ??
                      (action.labelTranslateKey ? (action.labelTranslateKey | translate) : '')
                  }}
                </button>
              }
            </span>
          }
        }

        <button
          type="button"
          mat-icon-button
          class="fb-global-header__action fb-icon-button--filled"
          data-testid="btn-toggle-theme"
          (click)="toggleTheme()"
          [attr.aria-label]="themeToggleLabel()"
          [matTooltip]="themeToggleLabel()">
          <mat-icon aria-hidden="true">{{ isDarkMode() ? 'light_mode' : 'dark_mode' }}</mat-icon>
        </button>

        @if (viewModel().printEnabled) {
          <button
            type="button"
            mat-icon-button
            class="fb-global-header__action fb-icon-button--filled"
            data-testid="btn-print-page"
            (click)="printPage()"
            [attr.aria-label]="'SHARED.SYSTEM.PRINT_PAGE' | translate"
            [matTooltip]="'SHARED.SYSTEM.PRINT_PAGE' | translate">
            <mat-icon aria-hidden="true">print</mat-icon>
          </button>
        }
        @if (viewModel().helpArticleId; as helpArticleId) {
          <button
            type="button"
            mat-icon-button
            class="fb-global-header__action fb-icon-button--filled"
            data-testid="btn-help"
            (click)="openHelpArticle(helpArticleId)"
            [attr.aria-label]="helpLabel()"
            [matTooltip]="helpLabel()">
            <mat-icon aria-hidden="true">help_outline</mat-icon>
          </button>
        }
      </div>
    }
  </div>
</header>
